import Modal from "@/components/atoms/Modal";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import { COMPANY_MEETING_TYPE, MeetingType } from "@/interfaces/Meeting";
import { useDestroyMeetingMutation } from "@/services/meetings";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { DateTime } from "luxon";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";

export interface MeetingDetailsData {
  id: string;
  title: string;
  startAt: string;
  type: MeetingType | null;
  authorId: string | null;
  authorName: string | null;
}

/**
 * Petite fiche d'un rendez-vous commercial sans entreprise (spec 2026-10-08,
 * §2.6). Un rendez-vous d'entreprise ouvre sa fiche entreprise ; celui-ci n'en
 * a pas, et sans cette modale on ne pourrait pas le supprimer.
 */
const MeetingDetailsModal: ForwardRefRenderFunction<{
  showOrHiddenModal: (data?: MeetingDetailsData) => void;
}> = (_, ref) => {
  const user = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [meeting, setMeeting] = useState<MeetingDetailsData | null>(null);
  const [destroyMeetingAsync] = useDestroyMeetingMutation();

  const showOrHiddenModal = (data?: MeetingDetailsData) => {
    if (data) setMeeting(data);
    ModalRef.current?.showOrHiddenModal();
  };

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  // Même règle que l'API : l'auteur, ADMIN et CSM.
  const canDelete =
    !!meeting &&
    (meeting.authorId === user?.id ||
      checkIfUserHasRoles(user, ["ADMIN", "CSM"]));

  // Deux modales ne se superposent jamais : on referme la fiche avant la
  // confirmation, et on la rouvre si l'on renonce — pas si c'est supprimé.
  const deletedRef = useRef(false);

  const onOpenDeleteModal = () => {
    deletedRef.current = false;
    ModalRef.current?.showOrHiddenModal();
    DeleteModalRef.current?.showOrHiddenModal(meeting?.id);
  };

  const onDeleteModalHidden = () => {
    if (deletedRef.current) return;
    ModalRef.current?.showOrHiddenModal();
  };

  const onDeleteMeeting = (id: string) => {
    const deletion = destroyMeetingAsync(id)
      .unwrap()
      .then((result) => {
        deletedRef.current = true;
        DeleteModalRef.current?.showOrHiddenModal();
        return result;
      });

    toast.promise(deletion, {
      pending: "Suppression du rendez-vous en cours...",
      success: "Le rendez-vous a bien été supprimé",
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          if (data?.status === 403) {
            return "Vous n'avez pas le droit de supprimer ce rendez-vous.";
          }
          return "Une erreur est survenue";
        },
      },
    });
  };

  const typeLabel =
    COMPANY_MEETING_TYPE.find((option) => option.value === meeting?.type)
      ?.label ?? COMPANY_MEETING_TYPE[0].label;

  return (
    <>
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDeleteMeeting}
        onHidden={onDeleteModalHidden}
        title="Souhaitez-vous vraiment supprimer ce rendez-vous ?"
      />
      <Modal
        ref={ModalRef}
        title={
          meeting ? (
            <>
              <span>{meeting.title}</span>
              <span className="fs--1 fw-normal text-600 mt-1">
                Rendez-vous sans entreprise
              </span>
            </>
          ) : (
            "Rendez-vous"
          )
        }
        beforeFooter={
          canDelete ? (
            <Button
              variant="outline-danger"
              className="me-auto"
              onClick={onOpenDeleteModal}
            >
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              Supprimer
            </Button>
          ) : undefined
        }
      >
        {meeting && (
          <dl className="row mb-0 fs--1">
            <dt className="col-sm-4 text-600">Date</dt>
            <dd className="col-sm-8">
              {DateTime.fromISO(meeting.startAt)
                .setLocale("fr")
                .toFormat("cccc d LLLL yyyy à HH:mm")}
            </dd>
            <dt className="col-sm-4 text-600">Type</dt>
            <dd className="col-sm-8">{typeLabel}</dd>
            <dt className="col-sm-4 text-600">Créé par</dt>
            <dd className="col-sm-8">{meeting.authorName ?? "Auteur inconnu"}</dd>
            {/* La règle plutôt qu'une liste : la direction et les mappings
                « Visibilité calendrier » le voient aussi, et une liste courte
                inviterait à mettre dans le titre ce qu'on croit privé. */}
            <dt className="col-sm-4 text-600">Visible par</dt>
            <dd className="col-sm-8 mb-0">
              {meeting.authorName
                ? `Les mêmes personnes qu'un rendez-vous dont ${meeting.authorName} serait le commercial.`
                : "La direction, ADMIN et CSM."}
            </dd>
          </dl>
        )}
      </Modal>
    </>
  );
};

export default forwardRef(MeetingDetailsModal);
